//*****************************/
// NOTE
// The components to this page are currently separated
// in order to avoid a large swath of changes happening
// in a single location which makes for messy git operations
//*****************************/
import { Link } from '@fluentui/react/lib/Link';
import * as React from 'react';

import { useImmutableSelector } from '../../hooks/useImmutableSelector';
import {
    selectActiveLiveSiteEvents, selectQueriedTimestampUtc, selectRecentUpdates, selectServices, selectStatuses,
} from '../../selectors/status/status';
import { isUndefinedOrWhiteSpace } from '../../utilities/strings';
import { StatusCurrentList } from '../status/StatusCurrentList';
import { StatusCurrentToggle } from '../status/StatusCurrentToggle';
import { StatusLegend } from '../status/StatusLegend';
import { StatusMatrix } from '../status/StatusMatrix';
import { StatusRecentUpdates } from '../status/StatusRecentUpdates';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';

interface StatusCardProps {
    style?: React.CSSProperties;
    className?: string;
}

interface StatusViewProps {
    liveSiteEventUrl: (id: number) => string;
}

// TODO: create a slot of the footer area
const StatusCard: React.FunctionComponent<StatusCardProps> = props => {
    const { children, style } = props;
    const className = isUndefinedOrWhiteSpace(props.className) ? '' : props.className;

    return (
        <div className={`ms-depth-8 status-view__card ${className}`} style={style}>
            {children}
        </div>
    );
};

/**
 * Arrange the components of the status page
 */
export function StatusView (props: StatusViewProps) {
    const { liveSiteEventUrl } = props;

    // Application state hooks
    const activeLiveSiteEvents = useImmutableSelector(selectActiveLiveSiteEvents);
    const queriedTimestampUtc = useImmutableSelector(selectQueriedTimestampUtc);
    const recentUpdates = useImmutableSelector(selectRecentUpdates);
    const services = useImmutableSelector(selectServices);
    const statuses = useImmutableSelector(selectStatuses);

    return (
        <>
            <div className="status-view__hero-section">
                <div className="ms-Grid status-view__container" dir="ltr">
                    <div className="ms-Grid-row status-view__max-lg-min">
                        <div className="ms-Grid-col status-view__current-incidents">
                            <StatusCard className="status-view__current-toggle">
                                <StatusCurrentToggle
                                    activeLiveSiteEvents={activeLiveSiteEvents}
                                    liveSiteEventUrl={liveSiteEventUrl} />
                            </StatusCard>
                            <div className="status-view__current-toggle-link">
                                <Link href="#recent-updates" className="status-view__link" styles={{ root: { color: '#0072c9' }}}>See all recent updates</Link>
                            </div>
                        </div>
                    </div>
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col status-view__matrix">
                            <StatusCard>
                                <StatusMatrix
                                    services={services}
                                    statuses={statuses} />

                                <div className="status-view__last-updated-timestamp">
                                    Last updated | {formatUtcAsLocalInEnglishLocale(queriedTimestampUtc)}
                                </div>

                                <div className="status-view__card-footer">
                                    <div>
                                        <StatusLegend />
                                    </div>

                                    <div className="status-view__see-recent-link status-view__min-lg-min">
                                        <Link href="#recent-updates" className="status-view__link">See all recent updates</Link>
                                    </div>
                                </div>
                            </StatusCard>
                        </div>
                        <div className="ms-Grid-col status-view__current-incidents status-view__min-lg-min">
                            <StatusCard>
                                <div>
                                    <StatusCurrentList
                                        activeLiveSiteEvents={activeLiveSiteEvents}
                                        liveSiteEventUrl={liveSiteEventUrl} />
                                </div>
                            </StatusCard>
                        </div>
                    </div>
                    <div className="container-divider"></div>
                </div>
            </div>
            <div style={{ backgroundColor: '#fff' }}>
                <div className="status-view__recent-updates-grid ms-Grid" dir="ltr">
                    <div className="ms-Grid-row">
                        <h2 id="recent-updates" className="status-view__recent-updates-header ms-Grid-col" style={{ width: '100%' }}>
                            Recent updates
                        </h2>
                    </div>
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col" style={{ width: '100%' }}>
                            <StatusRecentUpdates
                                recentUpdates={recentUpdates}
                                services={services} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
